import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Shield, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("6 caractères minimum");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lien invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-lg bg-[#E5B85C] flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#0B0F17]" strokeWidth={2.5} />
          </div>
          <div className="font-display font-black text-2xl uppercase">ASS les Régionnaires</div>
        </div>

        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-3">
          Réinitialisation
        </div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tight mb-3">
          Nouveau mot de passe
        </h1>
        <p className="text-slate-400 mb-8 text-sm">
          Choisis un nouveau mot de passe pour ton compte.
        </p>

        {!token ? (
          <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm">
            Lien invalide. Recommence depuis l'écran "Mot de passe oublié".
          </div>
        ) : done ? (
          <div
            data-testid="reset-done"
            className="p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm"
          >
            <div className="flex items-center gap-2 font-bold mb-1">
              <CheckCircle2 className="w-4 h-4" /> Mot de passe mis à jour
            </div>
            Redirection vers la connexion...
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                Nouveau mot de passe
              </label>
              <input
                data-testid="reset-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                Confirmer
              </label>
              <input
                data-testid="reset-confirm-input"
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div
                data-testid="reset-error"
                className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm"
              >
                {error}
              </div>
            )}
            <button
              data-testid="reset-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg disabled:opacity-50"
            >
              {loading ? "Mise à jour..." : "Mettre à jour"}
            </button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-slate-400">
          <Link
            to="/login"
            className="text-slate-300 hover:text-[#E5B85C] font-semibold"
          >
            ← Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
}
